import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import { matchingConfig } from './matching/config'
import { getFieldSynonyms } from './matching/field-synonyms'
import type { FieldCandidate } from './matching/types'
import { AnalysisStep } from './steps/AnalysisStep'
import { BatchDiagnostic } from './steps/BatchDiagnostic'
import { ImportStep } from './steps/ImportStep'
import { MapFieldsStep } from './steps/MapFieldsStep'
import { SelectFileStep } from './steps/SelectFileStep'
import { CSV_STEP_COUNT, useCsvModal, ImportStatus } from './useCsvModal'
import { CsvImportContext, ID_ATTR_KEY } from './utils'
import { Button } from '@/Atomic/Molecules/Button'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { StepTimeline } from '@/Atomic/Molecules/StepTimeline'
import {
  WizardModalFooter,
  WizardModalFooterNav,
  WizardModalLayout,
  WizardModalStepContent,
} from '@/Atomic/Molecules/WizardModal'
import { Modal } from '@/Atomic/Organisms/Modal'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes, SchemaGroups } from '@/Data/typesV2'
import { getSchemaName } from '@/utils/dataClassMap'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

import './CSVModal.scss'

interface CSVModalProps {
  isOpen: boolean
  context: CsvImportContext
  listId?: string
  entityName?: DataClassName.COMPANY | DataClassName.PERSON
  onClose: () => void
  initialFile?: File
  onImportStatusChange?: (status: ImportStatus) => void
}

export const CSVModal: React.FC<CSVModalProps> = connect(
  ({
    isOpen,
    context,
    listId,
    entityName = DataClassName.PERSON,
    onClose,
    initialFile,
    onImportStatusChange,
  }) => {
    const { t } = useTranslation()

    const { schema } = getSchema(getSchemaName(entityName))
    const rawAttributes = schema?.get('attributes') as SchemaAttributes
    const rawGroups = schema?.get('groups') as SchemaGroups | undefined

    const localizedAttributes = useMemo(
      () => getLocalizedSchemaAttributes(rawAttributes),
      [rawAttributes],
    )

    const attributesInGroups = useMemo(
      () =>
        new Set(
          Object.values(rawGroups ?? {}).flatMap(
            (group) => group.attributeOrder,
          ),
        ),
      [rawGroups],
    )

    const entityAttributes: Option[] = useMemo(
      () => [
        { value: ID_ATTR_KEY, label: 'ID' },
        ...Object.entries(localizedAttributes ?? {})
          .filter(([key]) => attributesInGroups.has(key))
          .map(([key, attr]) => ({ value: key, label: attr.title }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ],
      [localizedAttributes, attributesInGroups],
    )

    // CRM field targets for the matching engine. Includes the synthetic ID
    // option so an exported "ID" column is auto-matched on re-import (CRM-1241).
    // Carries a minimized schema slice (type + enum option values) so the AI
    // matcher can disambiguate near-synonym enum fields (group/category/status)
    // from a column's actual values, not just its label. `aliases` is the flat
    // list of every non-primary way a header may name this field: all localized
    // titles (en/de/fr/pl, from the schema) plus the curated synonyms for the
    // entity (from field-synonyms). The offline matcher tests headers against it,
    // so a French or abbreviated header resolves regardless of UI language.
    const fieldCandidates: FieldCandidate[] = useMemo(() => {
      const synonyms = getFieldSynonyms(entityName)
      return entityAttributes.map((a) => {
        const key = String(a.value)
        const attr = localizedAttributes?.[key]
        const options = attr?.enumValues?.length
          ? attr.enumValues.map((ev) => ({
              value: ev.value ?? '',
              label: ev.title ?? ev.value ?? '',
            }))
          : undefined
        const localizedTitles = rawAttributes?.[key]?.localizedTitles
        const aliases = [
          ...new Set([
            ...Object.values(localizedTitles ?? {}).filter(Boolean),
            ...(synonyms[key] ?? []),
          ]),
        ]
        return {
          key,
          label: a.label,
          ...(aliases.length ? { aliases } : {}),
          ...(attr?.type ? { type: attr.type } : {}),
          ...(options ? { options } : {}),
        }
      })
    }, [entityAttributes, entityName, localizedAttributes, rawAttributes])

    const mandatoryAttributes = useMemo(
      () =>
        new Set(
          Object.entries(localizedAttributes ?? {})
            .filter(
              ([key, attr]) => attr.mandatory && attributesInGroups.has(key),
            )
            .map(([key]) => key),
        ),
      [localizedAttributes, attributesInGroups],
    )

    const {
      step,
      file,
      csvColumns,
      columnMappings,
      columnMatchConfidence,
      columnModelConfidence,
      isMatchingAI,
      csvAnalysis,
      useFirstRowAsHeaders,
      setUseFirstRowAsHeaders,
      useEmailAsKey,
      setUseEmailAsKey,
      hasEmailTarget,
      stepTitles,
      importStatus,
      createdCount,
      updatedCount,
      failedCount,
      failedImportRowValues,
      importDuration,
      totalToImport,
      analysisBanner,
      isNextDisabled,
      rowLimitExceeded,
      isImportStep,
      handleFileSelect,
      handleNext,
      handleBack,
      handleClose,
      handleMappingChange,
      runBatchDiagnostic,
    } = useCsvModal({
      context,
      listId,
      entityName,
      onClose,
      mandatoryAttributes,
      schemaAttributes: localizedAttributes,
      fieldCandidates,
      initialFile,
      onImportStatusChange,
    })

    // Dropdown targets: same as entityAttributes, minus the synthetic ID option
    // when email is the update key (email matching never uses _id).
    // fieldCandidates still keeps _id (re-import auto-match).
    const dropdownAttributes: Option[] = useMemo(
      () =>
        useEmailAsKey
          ? entityAttributes.filter((a) => a.value !== ID_ATTR_KEY)
          : entityAttributes,
      [entityAttributes, useEmailAsKey],
    )

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOutsideClick={importStatus !== 'running'}
      >
        <Modal.Header title={t('components.csv.importTitle')} />

        <Modal.Body>
          <WizardModalLayout className="csv-modal-layout">
            <StepTimeline
              activeStep={step}
              className="csv-modal-timeline"
              steps={stepTitles.slice(0, CSV_STEP_COUNT)}
            />

            <WizardModalStepContent>
              {step === 0 && (
                <SelectFileStep
                  file={file}
                  onFileSelect={handleFileSelect}
                  rowLimitExceeded={rowLimitExceeded}
                  isMatchingAI={isMatchingAI}
                  diagnostic={
                    matchingConfig.batchDiagnosticEnabled ? (
                      <BatchDiagnostic
                        onRun={runBatchDiagnostic}
                        disabled={isMatchingAI}
                      />
                    ) : undefined
                  }
                />
              )}
              {step === 1 && (
                <MapFieldsStep
                  csvColumns={csvColumns}
                  columnMappings={columnMappings}
                  columnMatchConfidence={columnMatchConfidence}
                  columnModelConfidence={columnModelConfidence}
                  isMatchingAI={isMatchingAI}
                  useFirstRowAsHeaders={useFirstRowAsHeaders}
                  entityAttributes={dropdownAttributes}
                  useEmailAsKey={useEmailAsKey}
                  hasEmailTarget={hasEmailTarget}
                  onHeadersToggle={setUseFirstRowAsHeaders}
                  onEmailKeyToggle={setUseEmailAsKey}
                  onMappingChange={handleMappingChange}
                />
              )}
              {step === 2 && (
                <AnalysisStep
                  csvAnalysis={csvAnalysis}
                  csvColumns={csvColumns}
                  columnMappings={columnMappings}
                  entityAttributes={dropdownAttributes}
                  banner={analysisBanner}
                />
              )}
              {step === 3 && (
                <ImportStep
                  importStatus={importStatus}
                  createdCount={createdCount}
                  updatedCount={updatedCount}
                  failedCount={failedCount}
                  totalToImport={totalToImport}
                  importDuration={importDuration}
                  failedImportRowValues={failedImportRowValues}
                  csvColumns={csvColumns}
                  columnMappings={columnMappings}
                />
              )}
            </WizardModalStepContent>
          </WizardModalLayout>
        </Modal.Body>

        <Modal.Footer>
          <WizardModalFooter>
            {isImportStep ? (
              <Button
                variant="primary"
                onClick={handleClose}
                disabled={importStatus === 'running'}
              >
                {t('common.gotIt')}
              </Button>
            ) : (
              <>
                <Button variant="outlined" onClick={handleClose}>
                  {t('common.cancel')}
                </Button>
                <WizardModalFooterNav>
                  {step > 0 && (
                    <Button variant="outlined" onClick={handleBack}>
                      {t('common.back')}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    disabled={isNextDisabled}
                  >
                    {step === 2
                      ? t('components.csv.importButton')
                      : t('common.next')}
                  </Button>
                </WizardModalFooterNav>
              </>
            )}
          </WizardModalFooter>
        </Modal.Footer>
      </Modal>
    )
  },
)
